import { useEffect } from 'react';

import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { formatMoney } from '../../../utils/currency';
import type { WithdrawalRow } from '../../../types/admin';
import {
  WITHDRAWAL_PAYOUT_POLL_INTERVAL_MS,
  WITHDRAWAL_PAYOUT_POLL_MAX_ATTEMPTS,
} from '../../../utils/paymentPolling';

export type WithdrawalModalPhase = 'confirm-approve' | 'confirm-reject' | 'confirm-pay' | 'waiting-payout';

type Props = {
  open: boolean;
  phase: WithdrawalModalPhase;
  row: WithdrawalRow | null;
  isRetry?: boolean;
  isSubmitting?: boolean;
  pollAttempt?: number;
  errorMessage?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
};

function roleLabel(role: string) {
  if (role === 'APPRENANT') return 'Apprenant';
  if (role === 'FORMATEUR') return 'Formateur';
  if (role === 'PARTENAIRE_INSTITUTIONNEL') return 'Partenaire';
  return role;
}

function canalLabel(canal?: string | null) {
  if (canal === 'MOBILE_MONEY') return 'Mobile Money';
  if (canal === 'BANQUE') return 'Banque';
  return canal ?? '—';
}

export default function WithdrawalActionModal({
  open,
  phase,
  row,
  isRetry = false,
  isSubmitting = false,
  pollAttempt = 0,
  errorMessage = null,
  onConfirm,
  onCancel,
}: Props) {
  const isWaiting = phase === 'waiting-payout';
  const canDismiss = !isSubmitting && !isWaiting;

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && canDismiss) {
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, canDismiss, onCancel]);

  if (!open || !row) {
    return null;
  }

  const pollProgressPct =
    pollAttempt > 0
      ? Math.min(100, Math.round((pollAttempt / WITHDRAWAL_PAYOUT_POLL_MAX_ATTEMPTS) * 100))
      : 0;
  const pollRemainingSec = Math.max(
    0,
    Math.ceil(
      ((WITHDRAWAL_PAYOUT_POLL_MAX_ATTEMPTS - pollAttempt) * WITHDRAWAL_PAYOUT_POLL_INTERVAL_MS) / 1000,
    ),
  );

  const title =
    phase === 'waiting-payout'
      ? 'Confirmation FlexPay en cours'
      : phase === 'confirm-reject'
        ? 'Rejeter la demande'
        : phase === 'confirm-pay'
          ? 'Confirmer le paiement manuel'
          : isRetry
            ? 'Relancer le payout'
            : 'Approuver le retrait';

  const titleId = 'withdrawal-action-modal-title';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-[1px]"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && canDismiss) {
          onCancel();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md overflow-hidden rounded-2xl border border-outline-variant/20 bg-surface-container-lowest shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`border-b border-outline-variant/15 px-6 py-5 ${
            phase === 'confirm-reject'
              ? 'bg-red-50/80'
              : phase === 'waiting-payout'
                ? 'bg-primary/5'
                : phase === 'confirm-pay'
                  ? 'bg-sky-50/80'
                  : 'bg-emerald-50/70'
          }`}
        >
          <div className="flex items-start gap-3">
            <span
              className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                phase === 'confirm-reject'
                  ? 'bg-red-100 text-red-700'
                  : phase === 'waiting-payout'
                    ? 'bg-primary/10 text-primary'
                    : phase === 'confirm-pay'
                      ? 'bg-sky-100 text-sky-700'
                      : 'bg-emerald-100 text-emerald-700'
              }`}
            >
              {phase === 'waiting-payout' ? (
                <span className="h-6 w-6 animate-spin rounded-full border-[3px] border-primary border-t-transparent" />
              ) : (
                <LucideSymbolIcon
                  name={
                    phase === 'confirm-reject'
                      ? 'cancel'
                      : phase === 'confirm-pay'
                        ? 'payments'
                        : isRetry
                          ? 'refresh'
                          : 'check_circle'
                  }
                  tone="inherit"
                  size={22}
                />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <h2 id={titleId} className="text-lg font-black text-slate-900">
                {title}
              </h2>
              <p className="mt-1 text-sm text-on-surface-variant">
                {phase === 'waiting-payout'
                  ? 'Validez la transaction sur votre téléphone FlexPay.'
                  : phase === 'confirm-reject'
                    ? 'Le demandeur sera notifié du rejet.'
                    : phase === 'confirm-pay'
                      ? 'Confirmez uniquement après avoir envoyé l’argent au demandeur.'
                      : 'Le payout sera envoyé via FlexPay après votre validation téléphonique.'}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 px-6 py-5">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <div>
              <dt className="text-[10px] font-bold uppercase tracking-wide text-on-surface-variant">Demandeur</dt>
              <dd className="mt-0.5 font-semibold text-slate-900">{row.utilisateur.name}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-bold uppercase tracking-wide text-on-surface-variant">Rôle</dt>
              <dd className="mt-0.5 font-medium text-slate-800">{roleLabel(row.utilisateur.role)}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-bold uppercase tracking-wide text-on-surface-variant">Référence</dt>
              <dd className="mt-0.5 font-mono text-xs font-semibold text-slate-800">{row.reference}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-bold uppercase tracking-wide text-on-surface-variant">Montant</dt>
              <dd className="mt-0.5 text-base font-black text-primary tabular-nums">
                {formatMoney(row.montant, row.devise)}
              </dd>
            </div>
            <div className="col-span-2">
              <dt className="text-[10px] font-bold uppercase tracking-wide text-on-surface-variant">Destination</dt>
              <dd className="mt-0.5 text-slate-800">
                {canalLabel(row.canalPayout)}
                {row.compteDestination ? (
                  <span className="text-on-surface-variant"> · {row.compteDestination}</span>
                ) : null}
              </dd>
            </div>
          </dl>

          {phase === 'waiting-payout' ? (
            <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-4">
              <p className="text-center text-sm font-medium text-slate-800">
                En attente de la confirmation sur votre téléphone…
              </p>
              <p className="mt-1 text-center text-xs text-on-surface-variant">
                Le statut de la demande ne changera qu’après validation FlexPay.
              </p>
              <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/80">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-500"
                  style={{ width: `${pollProgressPct}%` }}
                />
              </div>
              <p className="mt-2 text-center text-xs font-bold text-primary">
                Temps restant : {pollRemainingSec > 0 ? `${pollRemainingSec} s` : 'expiration…'}
              </p>
            </div>
          ) : phase === 'confirm-approve' ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
              <p className="flex items-start gap-2">
                <LucideSymbolIcon name="phone_iphone" tone="inherit" size={18} className="mt-0.5 shrink-0" />
                <span>
                  Après confirmation ici, vous aurez <strong>45 secondes</strong> pour valider le payout sur
                  votre téléphone. Sans validation, l’opération sera déclarée en échec.
                </span>
              </p>
            </div>
          ) : phase === 'confirm-pay' ? (
            <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-950">
              Le portefeuille de l’utilisateur sera débité. À utiliser après un virement Mobile Money effectué
              manuellement (mode sans API FlexPay payout).
            </div>
          ) : (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
              Cette action est définitive. La demande ne pourra plus être payée automatiquement.
            </div>
          )}

          {errorMessage ? (
            <p className="rounded-lg border border-error/30 bg-error/10 px-3 py-2 text-sm font-medium text-error">
              {errorMessage}
            </p>
          ) : null}
        </div>

        {!isWaiting ? (
          <div className="flex flex-col-reverse gap-2 border-t border-outline-variant/15 bg-surface-container-high/30 px-6 py-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="rounded-lg border border-outline-variant/30 px-4 py-2.5 text-sm font-bold text-on-surface transition-colors hover:bg-surface-container-high disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isSubmitting}
              className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold text-white shadow-md transition-all disabled:opacity-50 ${
                phase === 'confirm-reject'
                  ? 'bg-error hover:opacity-95'
                  : 'bg-primary hover:opacity-95'
              }`}
            >
              {isSubmitting ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Traitement…
                </>
              ) : phase === 'confirm-reject' ? (
                <>
                  <LucideSymbolIcon name="cancel" tone="inherit" size={16} />
                  Confirmer le rejet
                </>
              ) : phase === 'confirm-pay' ? (
                <>
                  <LucideSymbolIcon name="payments" tone="inherit" size={16} />
                  Confirmer le paiement
                </>
              ) : (
                <>
                  <LucideSymbolIcon name={isRetry ? 'refresh' : 'check_circle'} tone="inherit" size={16} />
                  {isRetry ? 'Relancer le payout' : 'Confirmer l’approbation'}
                </>
              )}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
